// eslint-disable-next-line import/no-unresolved
import initCelBrowser, { evaluateCelScript } from '@unthread-io/cel-browser';
import _ from 'lodash';

const wasmUrl = new URL(`/assets/wasm/cel_bg.wasm`, import.meta.url);

let wasm;

/**
 * Initialize the CEL browser
 * This is safe to call multiple times, it will only initialize once
 * Returns a promise that resolves when initialization is complete
 */
export async function init(reload: boolean = false) {
  if (wasm && !reload) {
    return wasm;
  }

  try {
    wasm = await initCelBrowser({ module_or_path: wasmUrl });
  } catch (err) {
    console.error('Failed to initialize CEL browser:', err);
  }

  return wasm;
}

export const CONDITIONS_TYPES = {
  SIMPLE: 'simple',
  ADVANCED: 'advanced',
} as const;

export function simpleConditionsToAdvanced(query: any, options?: { treatNullBooleansAsFalse?: boolean }): string {
  if ('leftValue' in query || 'leftValueFrom' in query) {
    const leftValue = ('leftValue' in query ? JSON.stringify(query.leftValue) : query.leftValueFrom) ?? null;
    const rightValue = ('rightValue' in query ? JSON.stringify(query.rightValue) : query.rightValueFrom) ?? null;

    switch (query.operator) {
      case 'equals':
        if (typeof query.rightValue === 'boolean' && options?.treatNullBooleansAsFalse === true) {
          return `(${leftValue} == null ? false : ${leftValue}) == ${rightValue}`;
        }

        return `${leftValue} == ${rightValue}`;
      case 'notEquals':
        if (typeof query.rightValue === 'boolean' && options?.treatNullBooleansAsFalse === true) {
          return `(${leftValue} == null ? false : ${leftValue}) != ${rightValue}`;
        }

        return `${leftValue} != ${rightValue}`;
      case 'greaterThan':
        return `${leftValue} > ${rightValue}`;
      case 'lessThan':
        return `${leftValue} < ${rightValue}`;
      case 'greaterThanOrEquals':
        return `${leftValue} >= ${rightValue}`;
      case 'lessThanOrEquals':
        return `${leftValue} <= ${rightValue}`;
      case 'in':
        return `${leftValue} in ${rightValue}`;
      case 'notIn':
        return `!(${leftValue} in ${rightValue})`;
      case 'contains':
        if (Array.isArray(query.rightValue) && query.rightValue.length > 0) {
          return `${query.rightValue.map((r) => `${JSON.stringify(r)} in (${leftValue} == null  ? [] : ${leftValue})`).join(' || ')}`;
        }

        return `${rightValue} in (${leftValue} == null  ? [] : ${leftValue})`;
      case 'notContains':
        if (Array.isArray(query.rightValue) && query.rightValue.length > 0) {
          return `${query.rightValue.map((r) => `!(${JSON.stringify(r)} in (${leftValue} == null  ? [] : ${leftValue}))`).join(' && ')}`;
        }

        return `!(${rightValue} in (${leftValue} == null  ? [] : ${leftValue}))`;
      case 'isNull':
        return `${leftValue} == null`;
      case 'isNotNull':
        return `${leftValue} != null`;
    }
  } else if ('and' in query && query.and?.length > 0) {
    return `(${query.and.map((f) => simpleConditionsToAdvanced(f, options)).join(' && ')})`;
  } else if ('or' in query && query.or?.length > 0) {
    return `(${query.or.map((f) => simpleConditionsToAdvanced(f, options)).join(' || ')})`;
  }

  return 'true';
}

export function evaluateConditions(
  input: any,
  conditions?: any,
  options?: { treatNullBooleansAsFalse?: boolean },
): boolean {
  if (!conditions) {
    return true;
  }

  if (!wasm) {
    console.warn('CEL evaluation called before initialization completed - returning default "true"');
    return true;
  }

  const advancedConditions =
    conditions.type === CONDITIONS_TYPES.ADVANCED
      ? conditions.advanced
      : simpleConditionsToAdvanced(conditions.simple, options);

  if (!advancedConditions.trim()) {
    return true;
  }

  try {
    return Boolean(evaluateCelScript(advancedConditions, input));
  } catch (e) {
    console.error('Failed to evaluate conditions', e);

    return false;
  }
}

export function extractTicketId(inputString: string): string | null {
  // used to extract ticket id from a string of form: "conversation.ticketTypeFields['ticket-field-id-as-a-string']"
  // rename this if you can find a more specific definition please
  const match = inputString.match(/\[\'([^\']+)\'\]/);
  if (match && match[1]) {
    return match[1];
  }
  return null;
}

/**
 * Evaluates field visibility using iterative logic to handle conditional dependencies.
 * This mirrors the backend visibility calculation logic to ensure consistency.
 *
 * @param fieldDefinitions - Array of custom field definitions
 * @param fieldValues - Current field values
 * @returns Array of visible fields
 */
export function getVisibleFields<T extends { id: string; conditions?: any }>(
  fieldDefinitions: T[],
  fieldValues: Record<string, any> = {},
): T[] {
  // Mirror the backend visibility calculation logic exactly
  const results: Record<string, boolean> = {};
  const visibleInputValues: Record<string, any> = {};
  let isVisibilityUpdated = false;

  // Initialize all fields as not visible
  for (const field of fieldDefinitions) {
    results[field.id] = false;
  }

  const getFieldValue = (fieldId: string): any => {
    const value = fieldValues[fieldId];
    return value === '' ? undefined : value;
  };

  const markVisible = (fieldId: string): void => {
    if (!results[fieldId]) {
      results[fieldId] = true;
      // Update inputs based on visibility
      visibleInputValues[fieldId] = getFieldValue(fieldId);
      // Mark that visibility was changed
      isVisibilityUpdated = true;
    }
  };

  const markNotVisible = (fieldId: string): void => {
    if (results[fieldId]) {
      results[fieldId] = false;
      // Remove from visible inputs
      delete visibleInputValues[fieldId];
      // Mark that visibility was changed
      isVisibilityUpdated = true;
    }
  };

  // Iterative evaluation - loop until no more visibility changes occur
  do {
    // Reset state
    isVisibilityUpdated = false;

    // Check all fields in each loop
    for (const field of fieldDefinitions) {
      // Conditions exist if they are advanced or if there are keys on the simple condition object
      const hasConditionalVisibility =
        field.conditions &&
        (field.conditions.type === 'advanced' ||
          (field.conditions.type === 'simple' && Object.keys(field.conditions.simple || {}).length));

      if (!hasConditionalVisibility && !results[field.id]) {
        // If there are no conditions for visibility, mark visible
        markVisible(field.id);
        continue;
      }

      // Otherwise, determine visibility based on evaluation of conditions
      const isVisible = evaluateConditions(
        {
          conversation: {
            ticketTypeFields: visibleInputValues,
          },
        },
        field.conditions,
        { treatNullBooleansAsFalse: false }, // Use original behavior for form rendering
      );

      // If the visibility does not match current state, update it
      if (isVisible !== results[field.id]) {
        if (isVisible) {
          markVisible(field.id);
        } else {
          markNotVisible(field.id);
        }
      }
    }
  } while (isVisibilityUpdated);

  // Return only the visible fields
  return fieldDefinitions.filter((field) => results[field.id]);
}
